import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import UnitechHeader from "../../image/certificate_header.png";
import UnitechFooter from "../../image/certificate_footer.png";

let config = {
  margin: 15,
  borderMagin: 3,
  fontSize: 10,
  headerHeight: 70,
  footerHeight: 60,
  bordergap: 3,
  currentY: 0,
};

// Helper function to draw the header and footer images
function addHeaderFooter(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let letterHead = UnitechHeader;

  doc.addImage(letterHead, "PNG", 0, 0, pageWidth, config.headerHeight);
  doc.addImage(
    UnitechFooter,
    "PNG",
    0,
    pageHeight - config.footerHeight,
    pageWidth,
    config.footerHeight
  );
}

function drawCertificateInfoSection(doc, extraData, userMap, startY) {
  const details = [
    {
      label: "Instrument Name",
      value:
        extraData?.certificatePage?.instrumentName ||
        extraData?.certificatePage?.requestedname ||
        "",
    },
    {
      label: "DUCID / Serial Number",
      value:
        extraData?.ducid && extraData?.serialNumber
          ? `${extraData.ducid} / ${extraData.serialNumber}`
          : extraData?.ducid || extraData?.serialNumber || "",
    },
    {
      label: "Inward Date",
      value: extraData?.certificatePage?.entryDate
        ? moment(extraData?.certificatePage?.entryDate).format("DD-MM-YYYY")
        : "",
    },
    {
      label: "Created By",
      value: userMap?.[extraData?.certificatePage?.createdby] || "",
    },
    {
      label: "Calibrated By",
      value: userMap?.[extraData?.certificatePage?.calibratedby] || "",
    },
    {
      label: "Calibration Date",
      value: extraData?.certificatePage?.calibrationDate
        ? moment(extraData?.certificatePage?.calibrationDate).format(
            "DD-MM-YYYY, hh:mm A"
          )
        : "",
    },
    {
      label: "Approved By",
      value: userMap?.[extraData?.certificatePage?.approvedby] || "",
    },
    {
      label: "Approved Date",
      value: extraData?.certificatePage?.approvedDate
        ? moment(extraData?.certificatePage?.approvedDate).format(
            "DD-MM-YYYY, hh:mm A"
          )
        : "",
    },
  ];

  startY += 5;

  doc.setFontSize(config.fontSize);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);

  const colCount = 4;
  const colWidth =
    (doc.internal.pageSize.getWidth() - 2 * config.margin) / colCount;
  const rowHeight = 25;

  for (let i = 0; i < details.length; i++) {
    const colIndex = i % colCount;
    const rowIndex = Math.floor(i / colCount);
    const extraRowSpacing = rowIndex === 1 ? 5 : 0;
    const x = config.margin + colWidth * colIndex;
    const y = startY + rowHeight * rowIndex + extraRowSpacing;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(config.fontSize);
    doc.text(`${details[i].label}:`, x, y);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(config.fontSize - 1);
    const maxWidth = colWidth - 10;
    const splitText = doc.splitTextToSize(`${details[i].value}`, maxWidth);
    doc.text(splitText, x, y + 12);
  }

  return startY + rowHeight * Math.ceil(details.length / colCount) + 20;
}

export function generateAuditPDF({
  auditData,
  tableDataArray,
  extraData,
  userMap
}) {
  try {
    const doc = new jsPDF({
      orientation: "p",
      unit: "pt",
      format: "a4",
      compress: true,
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = config.headerHeight + 30;

    // First page header/footer
    addHeaderFooter(doc);

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(26, 35, 126);
    doc.text("AUDIT TRAIL", pageWidth / 2, y, { align: "center" });
    y += 15;

    doc.setDrawColor(26, 35, 126);
    doc.setLineWidth(2);
    doc.line(config.margin, y, pageWidth - config.margin, y);
    y += 25;

    if (extraData?.serviceReqNumber) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(config.fontSize + 2);
      doc.setTextColor(0, 0, 0);
      const srfText = `SRF No: ${extraData.serviceReqNumber}`;
      doc.text(srfText, pageWidth / 2, y, { align: "center" });
      y += 15;
    }

    if (extraData?.certificatePage) {
      y = drawCertificateInfoSection(doc, extraData, userMap, y);
    }

    tableDataArray.forEach((tableData, index) => {
      const audit = auditData[index];

      // Add new page if nearing footer
      if (y > doc.internal.pageSize.getHeight() - config.footerHeight - 100) {
        doc.addPage();
        addHeaderFooter(doc);
        y = config.headerHeight + 30;
      }

      y += 5;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(config.fontSize + 1);
      doc.setTextColor(26, 35, 126);
      const actionText = `Action: ${audit.action.toUpperCase()}`;
      const textWidth = doc.getTextWidth(actionText);
      const centerX = (pageWidth - textWidth) / 2;
      doc.text(actionText, centerX, y);
      y += 20;

      const details = [
        {
          label: "DUCID / Serial Number",
          value:
            extraData?.ducid && extraData?.serialNumber
              ? `${extraData.ducid} / ${extraData.serialNumber}`
              : extraData?.ducid || extraData?.serialNumber || "",
        },
        { label: "User", value: audit.userName },
        {
          label: "Time",
          value: moment(audit.lastModified).format("DD-MM-YYYY hh:mm A"),
        },
      ];

      doc.setFont("helvetica", "normal");
      doc.setFontSize(config.fontSize);
      doc.setTextColor(0, 0, 0);

      details.forEach((item) => {
        doc.setFont("helvetica", "bold");
        doc.text(`${item.label}: `, config.margin, y);
        const labelWidth = doc.getTextWidth(`${item.label}: `);
        doc.setFont("helvetica", "normal");
        doc.text(`${item.value}`, config.margin + labelWidth, y);
        y += 15;
      });

      y += 15;

      if (tableData && tableData.length > 0) {
        const filteredRows = tableData
          .filter((column) => column?.preValue !== column?.latestValue)
          .map((column) => {
            const row = [column?.name || ""];
            if (audit.action !== "create") {
              row.push(formatValue(column?.preValue));
            }
            row.push(formatValue(column?.latestValue));
            return row;
          });

        if (filteredRows.length > 0) {
          const headers = ["Column Name"];
          if (audit.action !== "create") headers.push("Previous Value");
          headers.push(audit.action === "create" ? "Value" : "Updated Value");

          autoTable(doc, {
            startY: y,
            head: [headers],
            body: filteredRows,
            styles: {
              fontSize: config.fontSize,
              cellPadding: 5,
              textColor: [0, 0, 0],
              lineWidth: 0.5,
              lineColor: [200, 200, 200],
              halign: "left",
              valign: "middle",
            },
            headStyles: {
              fillColor: [26, 35, 126],
              textColor: [255, 255, 255],
              fontStyle: "bold",
              fontSize: config.fontSize,
              halign: "center",
            },
            alternateRowStyles: {
              fillColor: [245, 245, 245],
            },
            theme: "striped",
            margin: {
              top: config.margin + config?.headerHeight + 2,
              bottom: config.footerHeight + 10,
              left: config.margin,
              right: config.margin,
            },
            columnStyles:
              audit.action === "create"
                ? { 0: { cellWidth: "auto" }, 1: { cellWidth: "auto" } }
                : {
                    0: { cellWidth: "auto" },
                    1: { cellWidth: "auto" },
                    2: { cellWidth: "auto" },
                  },
          });

          y = doc.previousAutoTable.finalY + 25;
        } else {
          doc.setFont("helvetica", "italic");
          doc.setFontSize(config.fontSize);
          doc.setTextColor(100, 100, 100);
          doc.text("No changes detected for this action.", config.margin, y);
          y += 20;
          doc.setTextColor(0, 0, 0);
        }
      }

      if (index < tableDataArray.length - 1) {
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(config.margin, y, pageWidth - config.margin, y);
        y += 20;
      }
    });

    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page++) {
      doc.setPage(page);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(config.fontSize - 2);
      doc.setTextColor(0);
      let content = `Page No : ${String(page).padStart(2, "0")} of ${String(
        totalPages
      ).padStart(2, "0")}`;
      doc.text(
        content,
        doc.internal.pageSize.getWidth() - config.margin - 70,
        config.headerHeight + config.margin - 2
      );
    }

    doc.save(`Audit_Trail.pdf`);
  } catch (error) {
    console.log("something went wrong download pdf", error);
  }
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string") {
    return value
      .replaceAll("||", ",")
      .replaceAll("|", " to ")
      .replaceAll("#", "  ");
  }
  return String(value);
}
